import {
  Box3,
  Group,
  Mesh,
  Vector3,
  type AnimationClip,
  type Material,
  type MeshStandardMaterial,
  type Object3D,
  type SkinnedMesh,
} from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

/**
 * Generated 3D props (`tools/assetgen`, TRELLIS-image-to-3D), loaded on demand.
 *
 * Every caller here is decorating something that already renders: a primitive locker, a
 * primitive view model, a capsule Hymn. So this module is **fail-soft by contract** —
 * `getProp` resolves to `null` on a 404, a decode failure or anything else, and the
 * caller keeps its stand-in. Nothing in here may ever reject: a cached rejection would
 * hand an unhandled rejection to every later caller, which is exactly the failure mode
 * the "the game must play if a GLB is missing" rule exists to prevent.
 *
 * The GLBs are meshopt-compressed and declare `EXT_meshopt_compression`,
 * `EXT_texture_webp` and `KHR_mesh_quantization` in `extensionsRequired`. GLTFLoader
 * handles webp and quantization itself; meshopt needs the decoder wired below (the
 * extension awaits `MeshoptDecoder.ready` internally, so callers must not). No Draco:
 * none of these assets use it.
 *
 * Materials stay `MeshStandardMaterial`. WebGPURenderer converts any non-node material
 * through `renderer.library.fromMaterial()`, which keys off `material.type` and copies
 * every property across, so a glTF standard material renders as a
 * `MeshStandardNodeMaterial` without us building one by hand.
 */

const MODEL_DIR = '/assets/gen/models/';

export type PropName =
  'lanternfall-crate' | 'extract-beacon' | 'ember-carbine' | 'wick-sidearm' | 'chorister';

/** Post-fit world-space size of a prop, in metres. Stashed on `userData.propSize`. */
export interface PropSize {
  x: number;
  y: number;
  z: number;
}

interface PropSpec {
  /**
   * Real-world size in metres of `axis`. TRELLIS normalises every mesh into a unit cube,
   * so a GLB carries no usable scale of its own and the true dimension has to come from
   * the design (see ASSETS.md's props rows).
   */
  readonly size: number;
  /** The axis `size` measures — always the model's longest, i.e. the normalised one. */
  readonly axis: 'x' | 'y' | 'z';
}

const SPECS: Record<PropName, PropSpec> = {
  'lanternfall-crate': { size: 1.0, axis: 'y' },
  'extract-beacon': { size: 3.5, axis: 'y' },
  'ember-carbine': { size: 0.75, axis: 'z' },
  'wick-sidearm': { size: 0.24, axis: 'x' },
  chorister: { size: 2.1, axis: 'y' },
};

/**
 * glTF's metallic/roughness defaults are 1/1 and these GLBs declare neither factor, so
 * GLTFLoader hands back fully-metallic surfaces. A metal contributes nothing diffuse,
 * which would render TRELLIS's baked-lighting albedo as near-black under the yard's
 * grazing 4.5° sun. The bake is essentially an albedo map, so treat it as one.
 */
const PROP_METALNESS = 0.15;
const PROP_ROUGHNESS = 0.7;

let loader: GLTFLoader | null = null;
function gltfLoader(): GLTFLoader {
  loader ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  return loader;
}

/** One in-flight (then settled) promise per prop; every `getProp` clones off it. */
const templates = new Map<PropName, Promise<Object3D | null>>();

/**
 * The fitted size of a prop returned by `getProp`. Zeroes for anything else, so a caller
 * that lost the race with a teardown divides by nothing surprising.
 */
export function propSize(prop: Object3D): PropSize {
  const s = prop.userData['propSize'] as Partial<PropSize> | undefined;
  return { x: s?.x ?? 0, y: s?.y ?? 0, z: s?.z ?? 0 };
}

/**
 * A ready-to-parent copy of `name`, scaled to its real size, centred on x/z and grounded
 * at y = 0 — or `null` if the asset could not be loaded.
 *
 * The returned object is a **wrapper** whose own transform is untouched: the fit lives on
 * its child. Callers own `position`/`rotation`/`scale` on what they get back and can set
 * them freely without clobbering the fit.
 *
 * Geometry, materials and textures are shared with every other copy (a `clone()` of the
 * template), so N lockers cost one upload.
 */
export function getProp(name: PropName): Promise<Object3D | null> {
  let tpl = templates.get(name);
  if (!tpl) {
    tpl = loadTemplate(name);
    templates.set(name, tpl);
  }
  return tpl.then((t) => (t ? t.clone(true) : null));
}

async function loadTemplate(name: PropName): Promise<Object3D | null> {
  const url = `${MODEL_DIR}${name}.glb`;
  try {
    const gltf = await gltfLoader().loadAsync(url);
    return fit(gltf.scene, SPECS[name]);
  } catch (err) {
    // Resolve null rather than rethrow: see the fail-soft note at the top of the file.
    console.warn(`[stillsun] prop unavailable: ${url}; the stand-in stays`, err);
    return null;
  }
}

/** Scales `scene` to its real size, centres it on x/z and drops it onto y = 0. */
function fit(scene: Object3D, spec: PropSpec): Object3D {
  scene.traverse((o) => {
    // Duck-typed, not `instanceof`: `three` and `three/webgpu` share one `three.core.js`
    // today, but the loader comes in through a different entry point and a bundler that
    // ever split those would silently make an `instanceof` test match nothing at all.
    const mesh = o as Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const list: Material[] = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of list) {
      const std = mat as MeshStandardMaterial;
      if (typeof std.metalness !== 'number') continue;
      std.metalness = PROP_METALNESS;
      std.roughness = PROP_ROUGHNESS;
    }
  });

  // Measured before the scale is applied, so `updateWorldMatrix` inside `setFromObject`
  // sees the mesh at its authored size.
  const box = new Box3().setFromObject(scene);
  const size = box.getSize(new Vector3());
  const along = size[spec.axis];
  const k = along > 0 ? spec.size / along : 1;
  scene.scale.setScalar(k);
  scene.position.set(
    -((box.min.x + box.max.x) / 2) * k,
    -box.min.y * k,
    -((box.min.z + box.max.z) / 2) * k,
  );

  const wrapper = new Group();
  wrapper.add(scene);
  wrapper.userData['propSize'] = { x: size.x * k, y: size.y * k, z: size.z * k } satisfies PropSize;
  return wrapper;
}

// --------------------------------------------------------------------- characters
//
// Mixamo bodies for the remote players (`apps/client/public/assets/gen/characters/`,
// manifest.json lists them). Same fail-soft contract as the props above: a missing or
// broken GLB resolves `null` and `game/remote.ts` keeps its capsule.
//
// The four rigs share Mixamo's joint *names* (`mixamorig:*`, which GLTFLoader sanitises
// to `mixamorig*` on both node names and track names), so one clip drives all of them —
// but they do **not** share proportions. char-01's hips rest at y = 1.061, char-03's at
// 0.811. Every `anim-*.glb` was baked on char-01's rig, so its single `Hips.position`
// track has to be scaled by the target's hip-height ratio or the shorter characters
// float. `game/characters.ts` does that with `hipsRestY` measured here.

const CHAR_DIR = '/assets/gen/characters/';

export type CharacterId = 'char-01' | 'char-02' | 'char-03' | 'char-04';

/** Every clip in the characters manifest. One `anim-<name>.glb` each, 30 fps. */
export type ClipName =
  | 'idle'
  | 'aim-idle'
  | 'walk'
  | 'run'
  | 'sprint'
  | 'walk-back'
  | 'strafe-left'
  | 'strafe-right'
  | 'crouch-idle'
  | 'crouch-walk'
  | 'prone-idle'
  | 'jump'
  | 'fire'
  | 'reload'
  | 'hit'
  | 'mantle'
  | 'revive'
  | 'downed-idle'
  | 'death-forward'
  | 'death-backward';

export interface CharacterTemplate {
  /** Shared, never parented anywhere: clone it with `SkeletonUtils.clone`. */
  readonly scene: Object3D;
  /** Bind-pose height in model units, so a caller can scale to PLAYER_HEIGHT. */
  readonly height: number;
  /** Rest y of `mixamorigHips` — the denominator of the clip retarget ratio. */
  readonly hipsRestY: number;
}

const characterTemplates = new Map<CharacterId, Promise<CharacterTemplate | null>>();
const clipTemplates = new Map<ClipName, Promise<AnimationClip | null>>();

/**
 * The shared template for one character, or `null` if it could not be loaded.
 * Never rejects (see the fail-soft note at the top of the file).
 */
export function loadCharacter(id: CharacterId): Promise<CharacterTemplate | null> {
  let tpl = characterTemplates.get(id);
  if (!tpl) {
    tpl = loadRigTemplate(`${CHAR_DIR}${id}.glb`, 'box');
    characterTemplates.set(id, tpl);
  }
  return tpl;
}

/**
 * The shared, un-retargeted `AnimationClip` for one animation, or `null`. Callers must
 * treat it as immutable — `game/characters.ts` clones before it rescales or filters.
 */
export function loadClip(name: ClipName): Promise<AnimationClip | null> {
  let clip = clipTemplates.get(name);
  if (!clip) {
    clip = loadClipTemplate(`${CHAR_DIR}anim-${name}.glb`, name);
    clipTemplates.set(name, clip);
  }
  return clip;
}

// ------------------------------------------------------------------------- hymns
//
// The same two loaders again, pointed at `apps/client/public/assets/gen/hymns/` — three
// Mixamo bodies for the Chorister, Cantor and Sexton plus one shared ten-clip set, built
// by the same `tools/assetgen` converter (`--raw-dir assets-src/mixamo/hymns/raw`). Same
// fail-soft contract as everything else here: a missing GLB resolves `null` and
// `game/entities.ts` keeps the primitive silhouette it was already drawing.

const HYMN_DIR = '/assets/gen/hymns/';

/** The three rigged Hymn bodies. The Verger keeps its hand-built furniture (see hymnrigs.ts). */
export type HymnCharacterId = 'char-chorister' | 'char-cantor' | 'char-sexton';

/** Every clip in the hymns manifest. One `anim-<name>.glb` each, 30 fps, in place. */
export type HymnClipName =
  'idle' | 'walk' | 'run' | 'attack' | 'cast' | 'hit' | 'stagger' | 'roar' | 'death-a' | 'death-b';

const hymnTemplates = new Map<HymnCharacterId, Promise<CharacterTemplate | null>>();
const hymnClips = new Map<HymnClipName, Promise<AnimationClip | null>>();

/** The shared template for one Hymn body, or `null`. Never rejects. */
export function loadHymnCharacter(id: HymnCharacterId): Promise<CharacterTemplate | null> {
  let tpl = hymnTemplates.get(id);
  if (!tpl) {
    tpl = loadRigTemplate(`${HYMN_DIR}${id}.glb`, 'skeleton');
    hymnTemplates.set(id, tpl);
  }
  return tpl;
}

/** The shared, un-retargeted clip for one Hymn animation, or `null`. Treat as immutable. */
export function loadHymnClip(name: HymnClipName): Promise<AnimationClip | null> {
  let clip = hymnClips.get(name);
  if (!clip) {
    clip = loadClipTemplate(`${HYMN_DIR}anim-${name}.glb`, name);
    hymnClips.set(name, clip);
  }
  return clip;
}

/**
 * Folds any `mixamorig<N>:` prefix variant back onto plain `mixamorig`. Today every
 * asset in this set already uses the bare prefix, so this is a no-op — it exists because
 * a single re-export through a different Mixamo/Blender path renumbers the armature and
 * would otherwise silently bind zero tracks.
 */
const RIG_PREFIX = /^mixamorig\d+/;

/**
 * How a rig's bind-pose height is measured.
 *
 * - `box`: the skinned mesh bounding box. What the four Waker rigs have always used, and
 *   right for them — they are clothed humans whose silhouette *is* their body.
 * - `skeleton`: the world y of `mixamorigHeadTop_End`, the joint every Mixamo skeleton ends
 *   on at the crown. Required for the Hymns: the Sexton (Mixamo's "Mutant") wears crystal
 *   blades that stand proud of its head in the T-pose, so its mesh box measures ~0.5 m
 *   taller than the body. Scaling by that box would shrink the body to fit — and since the
 *   same scalar is the denominator of the locomotion `timeScale` (see `game/hymnrigs.ts`),
 *   the feet would then outrun the stride. No prop can move a joint.
 */
type HeightSource = 'box' | 'skeleton';

async function loadRigTemplate(
  url: string,
  heightFrom: HeightSource,
): Promise<CharacterTemplate | null> {
  try {
    const gltf = await gltfLoader().loadAsync(url);
    const scene = gltf.scene;
    let hips: Object3D | null = null;
    let crown: Object3D | null = null;
    scene.traverse((o) => {
      o.name = o.name.replace(RIG_PREFIX, 'mixamorig');
      if (o.name === 'mixamorigHips') hips = o;
      if (o.name === 'mixamorigHeadTop_End') crown = o;
      const mesh = o as SkinnedMesh;
      if (!mesh.isSkinnedMesh && !(o as Mesh).isMesh) return;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      // Skinned bounds are computed from the *posed* vertices, so the matrices below have
      // to be current first; without them a KHR_mesh_quantization mesh measures as a unit
      // cube (the real scale lives in the inverse bind matrices, not the node transform).
      mesh.frustumCulled = true;
    });
    // Bone matrices first: `SkinnedMesh.computeBoundingBox/Sphere` walk every vertex
    // through `applyBoneTransform`, and `Box3.setFromObject` uses those bounds.
    scene.updateMatrixWorld(true);
    scene.traverse((o) => {
      const mesh = o as SkinnedMesh;
      if (!mesh.isSkinnedMesh) return;
      mesh.computeBoundingBox();
      mesh.computeBoundingSphere();
    });
    const box = new Box3().setFromObject(scene);
    const size = box.getSize(new Vector3());
    let height = size.y > 0.01 ? size.y : 1.7;
    if (heightFrom === 'skeleton' && crown) {
      // `updateMatrixWorld(true)` above already ran, so the world matrix is current. Falls
      // through to the box when the joint is missing rather than shipping a wrong scale.
      const y = (crown as Object3D).getWorldPosition(new Vector3()).y;
      if (y > 0.01) height = y;
    }
    // The character GLBs each carry their own one-frame `mixamo.com` clip; ignored.
    return { scene, height, hipsRestY: hips ? (hips as Object3D).position.y : 1 };
  } catch (err) {
    console.warn(`[stillsun] character unavailable: ${url}; the stand-in stays`, err);
    return null;
  }
}

// ------------------------------------------------------------------ asset packs
//
// The generalised loader for third-party CC0 packs imported by
// `tools/assetgen/src/packs.ts` into `apps/client/public/assets/gen/packs/`. The two
// loaders above are frozen around one pipeline each — Mixamo's `mixamorig*` naming, one
// clip per GLB, a hip-height retarget ratio. None of that survives contact with a pack
// somebody else rigged, so this section takes the three assumptions out:
//
//   - **Bone names come from a table, not a regex.** `RigAliasTable` is the JSON in
//     `tools/assetgen/rigs/*.json`, our canonical names on one side and the pack's on the
//     other. Nothing here knows the string "hips".
//   - **Every clip in the GLB is kept**, renamed through the table's clip map and returned
//     as a `Map<canonical, AnimationClip>`. KayKit ships 76-95 clips inside each body; the
//     old loader would have taken `animations[0]` and thrown the rest away.
//   - **Height is measured, not declared.** A `Box3` over the *skinned* meshes only.
//
// That last word is load-bearing. Every KayKit body GLB also carries its equipment as
// un-skinned sibling meshes under the rig root — `1H_Sword`, `Knight_Helmet`, `Mage_Hat`
// and friends. No clip animates them and no bone parents them, so they sit motionless at
// the origin: a `Box3` over the whole scene measures a two-handed sword standing on end
// (2.0 units against the body's 1.3), and anything rendered as-loaded shows a sword lying
// on the floor between the character's feet. `loadPackRig` hides them and hands them back
// by name instead; `attachTo` is the supported way to put something in a hand.
//
// Same fail-soft contract as everything else in this file: `null` on any failure, never a
// rejection, and the caller keeps whatever stand-in it was already drawing.

const PACK_DIR = '/assets/gen/packs/';

/** One entry of the alias table's `sockets` block. */
export interface RigSocket {
  /** The pack's bone name this socket rides on. */
  readonly bone: string;
  /** Optional local offset from that bone, in model units. */
  readonly offset?: readonly [number, number, number] | undefined;
  /**
   * Optional local rotation `(x, y, z, w)` — the conjugate of the bone's world rest
   * rotation, so a piece attached here stands upright in **model space** at rest instead
   * of inheriting the bone's own frame. KayKit needs one on four of its nine sockets: the
   * upper arms point sideways down a T-pose and the hand slots are rolled 90° about Z.
   * Without it, gear has to be authored in each bone's rotated frame, which is both
   * miserable to reason about and different for the left and right side of the same pair.
   */
  readonly rotation?: readonly [number, number, number, number] | undefined;
}

/** One entry of the alias table's `clips` block. */
export interface RigClipAlias {
  /** The clip's name inside the GLB. */
  readonly name: string;
  readonly duration?: number | undefined;
  readonly loop?: boolean | undefined;
}

/**
 * `tools/assetgen/rigs/<pack>.json`, as imported by the caller. Only the fields this
 * loader reads are typed; the file carries more (rest translations, the deform-bone
 * subset, the loose-mesh inventory) for `tools/assetgen/src/bake-anim.ts`.
 */
export interface RigAliasTable {
  /** canonical bone name -> the pack's name, or `null` where the rig has no such joint. */
  readonly bones: Readonly<Record<string, string | null>>;
  /** What to use instead where `bones` is `null` (KayKit has no neck and no clavicles). */
  readonly boneFallbacks?: Readonly<Record<string, string>> | undefined;
  readonly sockets: Readonly<Record<string, RigSocket>>;
  /** canonical clip name -> the clip as the pack names it. */
  readonly clips: Readonly<Record<string, RigClipAlias>>;
  /** Extra clips only some bodies in the pack carry; merged in when present. */
  readonly skeletonsOnly?: Readonly<Record<string, RigClipAlias>> | undefined;
}

export interface PackRig {
  /** Shared and never parented anywhere: clone it with `SkeletonUtils.clone`. */
  readonly scene: Object3D;
  /**
   * Bind-pose height in model units, measured over the skinned meshes only — see the note
   * above on why the whole-scene box is wrong for these packs.
   */
  readonly height: number;
  /** Every clip in the GLB, keyed by our canonical name where the table has one. */
  readonly clips: ReadonlyMap<string, AnimationClip>;
  /** Clips the alias table did not name, keyed by the pack's own name. Nothing is dropped. */
  readonly extraClips: ReadonlyMap<string, AnimationClip>;
  /** Canonical bone name -> the bone in `scene`. Fallbacks already applied. */
  readonly bones: ReadonlyMap<string, Object3D>;
  /** Canonical socket name -> the bone it rides on, with the table's offset applied. */
  readonly sockets: ReadonlyMap<string, RigSocket & { readonly node: Object3D }>;
  /**
   * The un-skinned meshes the pack bundles into the body (weapons, helmets, capes), by
   * name. All are `visible = false` on the template: show one to use it as a free look.
   */
  readonly loose: ReadonlyMap<string, Object3D>;
}

const packRigs = new Map<string, Promise<PackRig | null>>();
const packProps = new Map<string, Promise<Object3D | null>>();

/**
 * A rigged body from an asset pack, or `null` if it could not be loaded. Never rejects.
 *
 * `url` is either a full path or a name relative to `/assets/gen/packs/`, so
 * `loadPackRig('adventurers/knight', kaykit)` is the normal call. One template per url is
 * cached; clone what you get back with `SkeletonUtils.clone` before parenting it, exactly
 * as `game/characters.ts` does for the Mixamo rigs.
 */
export function loadPackRig(url: string, aliases: RigAliasTable): Promise<PackRig | null> {
  const resolved = packUrl(url);
  let rig = packRigs.get(resolved);
  if (!rig) {
    rig = loadPackRigTemplate(resolved, aliases);
    packRigs.set(resolved, rig);
  }
  return rig;
}

/**
 * A static prop from an asset pack, or `null`. Never rejects.
 *
 * Unlike `getProp` above, **nothing is fitted**: KayKit and Kenney author in metres and
 * their kits only tile because a wall is exactly 1 m wide. Normalising these into a unit
 * cube — which is the right thing to do to a TRELLIS mesh, and why `getProp` does it —
 * would make every piece of Lanternfall a different size. The returned object is the
 * loaded scene at its authored scale, cloned off a shared template.
 */
export function loadPackProp(url: string): Promise<Object3D | null> {
  const resolved = packUrl(url);
  let tpl = packProps.get(resolved);
  if (!tpl) {
    tpl = loadPackPropTemplate(resolved);
    packProps.set(resolved, tpl);
  }
  return tpl.then((t) => (t ? t.clone(true) : null));
}

function packUrl(url: string): string {
  if (url.startsWith('/') || url.startsWith('http')) return url;
  return `${PACK_DIR}${url}${url.endsWith('.glb') ? '' : '.glb'}`;
}

async function loadPackPropTemplate(url: string): Promise<Object3D | null> {
  try {
    const gltf = await gltfLoader().loadAsync(url);
    gltf.scene.traverse((o) => {
      const mesh = o as Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      // No metalness/roughness override here, unlike `fit()` above. That override exists
      // because a TRELLIS bake is an albedo map wearing a PBR material's clothing. These
      // packs ship deliberate, hand-set material values against a gradient atlas, and
      // flattening them would erase the one thing that makes the style read.
    });
    return gltf.scene;
  } catch (err) {
    console.warn(`[stillsun] pack prop unavailable: ${url}; the stand-in stays`, err);
    return null;
  }
}

async function loadPackRigTemplate(url: string, aliases: RigAliasTable): Promise<PackRig | null> {
  try {
    const gltf = await gltfLoader().loadAsync(url);
    const scene = gltf.scene;

    const byName = new Map<string, Object3D>();
    const skinned: SkinnedMesh[] = [];
    const loose = new Map<string, Object3D>();
    scene.traverse((o) => {
      byName.set(o.name, o);
      // Duck-typed rather than `instanceof`, for the reason `fit()` gives above.
      const skin = o as SkinnedMesh;
      if (skin.isSkinnedMesh) {
        skinned.push(skin);
        skin.castShadow = true;
        skin.receiveShadow = true;
        skin.frustumCulled = true;
        return;
      }
      if ((o as Mesh).isMesh) {
        // An un-skinned mesh in a rigged GLB is bundled equipment, not body. Hidden by
        // default so it cannot render adrift at the origin; `loose` hands it back.
        o.visible = false;
        loose.set(o.name, o);
      }
    });

    // Bone matrices before bounds: `SkinnedMesh.computeBoundingBox` walks every vertex
    // through `applyBoneTransform`, and without current matrices a KHR_mesh_quantization
    // mesh measures as a unit cube (the real scale is in the inverse bind matrices, not
    // the node transform). Every GLB `packs.ts` writes is quantized, so this is not
    // optional here the way it nearly is for an unquantized asset.
    scene.updateMatrixWorld(true);
    const box = new Box3();
    for (const mesh of skinned) {
      mesh.computeBoundingBox();
      mesh.computeBoundingSphere();
      box.expandByObject(mesh);
    }
    const size = box.isEmpty() ? new Vector3(0, 0, 0) : box.getSize(new Vector3());
    const height = size.y > 0.01 ? size.y : 1.7;

    const bones = new Map<string, Object3D>();
    for (const [canonical, packName] of Object.entries(aliases.bones)) {
      const name = packName ?? aliases.boneFallbacks?.[canonical];
      if (name === undefined) continue;
      const node = byName.get(name);
      if (node) bones.set(canonical, node);
    }

    const sockets = new Map<string, RigSocket & { node: Object3D }>();
    for (const [canonical, socket] of Object.entries(aliases.sockets)) {
      const node = byName.get(socket.bone);
      if (node) sockets.set(canonical, { ...socket, node });
    }

    // Every clip is kept. Ones the table names are keyed canonically and renamed in place
    // so `AnimationAction` lookups and any later serialisation agree; the rest keep the
    // pack's own name and go in `extraClips` rather than being thrown away.
    const aliasByPackName = new Map<string, string>();
    for (const [canonical, alias] of Object.entries(aliases.clips)) {
      aliasByPackName.set(alias.name, canonical);
    }
    for (const [canonical, alias] of Object.entries(aliases.skeletonsOnly ?? {})) {
      if (!aliasByPackName.has(alias.name)) aliasByPackName.set(alias.name, canonical);
    }
    const clips = new Map<string, AnimationClip>();
    const extraClips = new Map<string, AnimationClip>();
    for (const clip of gltf.animations) {
      const canonical = aliasByPackName.get(clip.name);
      if (canonical === undefined) {
        extraClips.set(clip.name, clip);
        continue;
      }
      // First alias wins: `emote` aliases `Cheer` after `cheer` already claimed it, and
      // the clip object is shared, so renaming on the second pass would rename both.
      if (clips.has(canonical)) continue;
      clip.name = canonical;
      clips.set(canonical, clip);
    }

    return { scene, height, clips, extraClips, bones, sockets, loose };
  } catch (err) {
    console.warn(`[stillsun] pack rig unavailable: ${url}; the stand-in stays`, err);
    return null;
  }
}

/**
 * Parents `piece` to `rig`'s socket, applying the table's offset and rest-rotation
 * correction. Returns false if the rig has no such socket, so a caller can fall back
 * rather than silently drop the gear at the character's feet — the exact failure the
 * loose-mesh handling above exists to prevent.
 *
 * The piece is expected to be authored in **model space** with its origin at the socket:
 * +Y up, +Z forward, which is the convention KayKit's own weapon GLBs already follow
 * (`sword_1handed` spans y −0.366..1.409 about a grip at the origin) and the one
 * `tools/assetgen/blender/gear.py` builds to.
 *
 * `rig` must be the **clone** the caller is rendering, not the shared template.
 */
export function attachTo(rig: PackRig, socketName: string, piece: Object3D): boolean {
  const socket = rig.sockets.get(socketName);
  if (!socket) return false;
  const [x, y, z] = socket.offset ?? [0, 0, 0];
  piece.position.set(x, y, z);
  const q = socket.rotation;
  if (q) piece.quaternion.set(q[0], q[1], q[2], q[3]);
  socket.node.add(piece);
  return true;
}

async function loadClipTemplate(
  url: string,
  name: ClipName | HymnClipName,
): Promise<AnimationClip | null> {
  try {
    const gltf = await gltfLoader().loadAsync(url);
    const clip = gltf.animations[0];
    if (!clip) {
      console.warn(`[stillsun] clip GLB has no animation: ${url}`);
      return null;
    }
    clip.name = name;
    for (const track of clip.tracks) track.name = track.name.replace(RIG_PREFIX, 'mixamorig');
    return clip;
  } catch (err) {
    console.warn(`[stillsun] clip unavailable: ${url}`, err);
    return null;
  }
}
